import { useRef, useState } from "react";
import { ExperimentType } from "../../Types";
import { styles } from "./experimentDetails.styles";
import {
  injectWiggleAnimation,
  styles as sharedStyles,
} from "../../Shared/shared.styles";
import {
  CommandBar,
  ICommandBarItemProps,
  mergeStyles,
  Stack,
  Text,
} from "@fluentui/react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";
import ExperimentDetailTabs from "./ExperimentDetailTabs/ExperimentDetailTabs";
import ExperimentDetailsConfirmationModel from "./ExperimentDetailsConfirmationModel/ExperimentDetailsConfirmationModel";
import {
  ExperimentFormContext,
  useExperimentForm,
} from "../../Hooks/UseExperimentForm";
import { useNavigateEv2Experiments } from "../../Hooks/UseNavigateToEv2Experiments";
import { DEFAULT_EXPERIMENT } from "../../Shared/shared.consts";
import useAppInsights from "../../Hooks/UseAppInsights";
import { NavType } from "../../Shared/AppInsights";
import { useDataAccessContext } from "../../Contexts/DataAccessContext";

interface ExperimentDetailsProps {
  /**
   * List of all experiments. Used for validation and duplicate checks.
   */
  experiments: ExperimentType[];
  /**
   * The experiment data to display/edit, or undefined for new experiments (create)
   */
  experimentDetails?: ExperimentType;
  /**
   * When true, will show editable form with create/discard buttons
   */
  isCreate?: boolean;
  /**
   * When true, shows editable form with pre-filled data and create/discard buttons
   */
  isClone?: boolean;

  /**
   * When true, the form is read-only
   */
  isReadOnly?: boolean;
}

/**
 * Main experiment details form component that handles creating, editing, and cloning experiments.
 */
const ExperimentDetails: React.FC<ExperimentDetailsProps> = ({
  experiments,
  experimentDetails,
  isCreate = false,
  isClone = false,
  isReadOnly = false,
}) => {
  const { navigateToRoot } = useNavigateEv2Experiments();
  const { hasAdoAccess } = useDataAccessContext();
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  // if the user is editing the form, when false form is read-only
  const [isEditMode, setIsEditMode] = useState(isCreate || isClone);
  const [accordionIsOpen, setAccordionIsOpen] = useState(isEditMode);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const initialData = experimentDetails || DEFAULT_EXPERIMENT;
  const experimentFormContext: ExperimentFormContext = useExperimentForm({
    experiments,
    initialFormData: initialData,
    setShowConfirmationModal,
    setErrors,
    isClone,
    isCreate,
  });
  const {
    handleSave,
    formChanges: { hasChanged },
    saveWiggle,
    formState,
    setFormState,
    validateForm,
  } = experimentFormContext;

  const { trackNavigationEvent, trackOperationEvent } =
    useAppInsights("ExperimentDetails");

  const initialFileDataRef = useRef<ExperimentType>(
    experimentFormContext.initialFormState,
  );

  const handleEdit = () => {
    setIsEditMode(true);
    trackOperationEvent({
      operation: "Edit",
      function: "handleEdit",
      properties: { formState, isClone, isCreate, isReadOnly },
    });
  };

  const handleDiscard = () => {
    navigateToRoot();
    trackNavigationEvent({
      destination: "ExperimentsTable",
      navType: NavType.Internal,
      function: "handleDiscard",
      properties: { formState, isClone, isCreate, isReadOnly },
    });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setFormState(initialFileDataRef.current);
    // validate form to reset error and changed states
    validateForm(initialFileDataRef.current);
    trackOperationEvent({
      operation: "Cancel",
      function: "handleCancel",
      properties: { formState, isClone, isCreate, isReadOnly },
    });
  };

  const commandBarItems: ICommandBarItemProps[] =
    isCreate || isClone
      ? [
          // For new experiments: Create and Discard buttons
          {
            key: "create",
            text: "Create",
            iconProps: { iconName: "Add", style: { fontSize: "12px" } },
            onClick: handleSave,
            disabled: !hasChanged || isReadOnly,
            buttonStyles: {
              root: saveWiggle ? injectWiggleAnimation : undefined,
            },
            ariaLabel: "Create",
          },
          {
            key: "discard",
            text: "Discard",
            iconProps: { iconName: "Delete", style: { fontSize: "12px" } },
            onClick: handleDiscard,
            ariaLabel: "Discard",
          },
        ]
      : [
          // For existing experiments: Edit, Save, and Cancel buttons
          {
            key: "edit",
            text: "Edit details",
            iconProps: { iconName: "Edit", style: { fontSize: "12px" } },
            onClick: handleEdit,
            disabled: isEditMode || isReadOnly || !hasAdoAccess,
            ariaLabel: "Edit",
          },
          {
            key: "save",
            text: "Save details",
            iconProps: { iconName: "Save", style: { fontSize: "12px" } },
            onClick: handleSave,
            disabled: !isEditMode || !hasChanged || isReadOnly || !hasAdoAccess,
            buttonStyles: {
              root: saveWiggle ? injectWiggleAnimation : undefined,
            },
            ariaLabel: "Save",
          },
          {
            key: "cancel",
            text: "Cancel edit",
            iconProps: { iconName: "Cancel", style: { fontSize: "12px" } },
            onClick: handleCancel,
            disabled: !isEditMode || isReadOnly,
            ariaLabel: "Cancel",
          },
        ];

  return (
    <Stack
      className={mergeStyles(
        styles.experimentDetails,
        !accordionIsOpen && styles.experimentDetailsAccordionClosed,
      )}
    >
      <Accordion
        defaultOpenItems={isEditMode ? "experimentDetails" : undefined}
        collapsible
        className={mergeStyles(
          sharedStyles.flexOverflowContainer,
          styles.experimentDetailsDetails,
        )}
        onToggle={() => {
          setAccordionIsOpen((prev) => !prev);
          trackOperationEvent({
            operation: "AccordionToggle",
            function: "Accordion.ExperimentDetails.onToggle",
            properties: { isOpen: !accordionIsOpen },
          });
        }}
      >
        <AccordionItem
          value="experimentDetails"
          className={mergeStyles(
            sharedStyles.flexOverflowContainer,
            styles.experimentDetailsAccordionPanel,
          )}
        >
          <AccordionHeader className={styles.experimentDetailsAccordionHeader}>
            <Text
              variant="large"
              className={styles.experimentDetailsAccordionHeaderLabel}
            >
              Experiment Details
            </Text>
          </AccordionHeader>
          <AccordionPanel className={sharedStyles.flexOverflowContainer}>
            <CommandBar
              items={commandBarItems}
              className={styles.experimentDetailsCommandBar}
            />
            <ExperimentDetailTabs
              experimentFormContext={experimentFormContext}
              experiments={experiments}
              isEditMode={isEditMode}
              isCreate={isCreate}
              isClone={isClone}
              errors={errors}
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      {showConfirmationModal && (
        <ExperimentDetailsConfirmationModel
          isCreate={isCreate || isClone}
          isOpen={showConfirmationModal}
          extensionPrefix={experimentFormContext.extensionPrefix}
          onCancel={() => setShowConfirmationModal(false)}
          formData={formState}
        />
      )}
    </Stack>
  );
};

export default ExperimentDetails;
